import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Row,
  Col,
  Button,
  Form,
  Dropdown,
  ButtonGroup,
  Modal,
  Card,
} from "react-bootstrap";
import { MdAddchart } from "react-icons/md";
import { Link } from "react-router-dom";
import "./emp-info.css";
import { BsFillInfoCircleFill } from "react-icons/bs";
import { CgProfile } from "react-icons/cg";
import { FcOk } from "react-icons/fc";
import { FaEdit } from "react-icons/fa";
import { IoIosEye } from "react-icons/io";

export default function EmployeeInformation() {
  const rowsPerPage = 10;
  const [currentPage, setCurrentPage] = useState(1);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");

  // Sort employees alphabetically by name
  const sortEmployeesByName = (employees) => {
    return [...employees].sort((a, b) => {
      const nameA = a.name?.toUpperCase() || '';
      const nameB = b.name?.toUpperCase() || '';
      if (nameA < nameB) return -1;
      if (nameA > nameB) return 1;
      return 0;
    });
  };

  // Fetch all active employees initially
  useEffect(() => {
    const fetchEmployees = async () => {
      setLoading(true);
      try {
        const response = await axios.get(
            "https://backend.ems.hiradentallab.com/api/user/getAllUsers"
        );

        // Filter out inactive users and sort alphabetically
        const activeUsers = response.data.filter(user => user.status === "active");
        const sortedUsers = sortEmployeesByName(activeUsers);

        setData(sortedUsers);
        setSearchResults(sortedUsers);
      } catch (error) {
        console.error("Error fetching employees:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchEmployees();
  }, []);

  // Handle search query change and fetch search results
  useEffect(() => {
    const fetchSearchResults = async () => {
      if (searchQuery.trim() === "") {
        // If no search query, show all active sorted employees
        const activeUsers = data.filter(user => user.status === "active");
        setSearchResults(sortEmployeesByName(activeUsers));
        return;
      }

      try {
        const response = await axios.get(
            `https://backend.ems.hiradentallab.com/api/user/search?query=${searchQuery}`
        );
        // Filter out inactive users and sort search results alphabetically
        const activeSearchResults = response.data.filter(user => user.status === "active");
        const sortedResults = sortEmployeesByName(activeSearchResults);
        setSearchResults(sortedResults);
      } catch (err) {
        console.error("Error fetching search results:", err);
        setSearchResults([]);
      }
    };

    const timeoutId = setTimeout(fetchSearchResults, 500); // 500ms debounce delay
    return () => clearTimeout(timeoutId); // Cleanup the timeout
  }, [searchQuery, data]);

  // Handle page change
  const handlePageChange = (pageNumber) => {
    if (
        pageNumber >= 1 &&
        pageNumber <= Math.ceil(searchResults.length / rowsPerPage)
    ) {
      setCurrentPage(pageNumber);
    }
  };

  // Handle form submission without reloading the page
  const handleSubmit = (event) => {
    event.preventDefault();
  };

  // Calculate page range
  const pageRange = () => {
    const pages = [];
    const totalPages = Math.ceil(searchResults.length / rowsPerPage);
    let startPage = Math.max(currentPage - 1, 1);
    let endPage = Math.min(startPage + 2, totalPages);

    if (endPage - startPage < 2) {
      startPage = Math.max(endPage - 2, 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return pages;
  };

  const handleShow = (employeeData) => {
    setSelectedEmployee(employeeData);
    setShowDialog(true);
  };

  const handleClose = () => setShowDialog(false);

  return (
      <div className="container-fluid">
        <h1 className="emp-info-title">
          <BsFillInfoCircleFill className="settings-icon" /> Employee Information
        </h1>

        <hr />

        <h1 className="emp-info-subtitle">
          <CgProfile className="settings-icon" /> Employee
        </h1>

        <hr />

        <div className="emp-info-content">
          <Link to="/add-emp">
            <Button variant="primary" className="add-emp-button">
              <MdAddchart className="button-icon" /> Add Employee
            </Button>
          </Link>

          <div className="emp-info-actions">
            <Row>
              <Col xs={12} md={6} className="d-flex align-items-center mb-2">
                <Dropdown as={ButtonGroup}>
                  <Button variant="primary">Show Entries</Button>

                  <Dropdown.Toggle
                      split
                      variant="primary"
                      id="dropdown-split-basic"
                  />

                  <Dropdown.Menu>
                    <Dropdown.Item href="#/action-1">10</Dropdown.Item>
                    <Dropdown.Item href="#/action-2">25</Dropdown.Item>
                    <Dropdown.Item href="#/action-3">50</Dropdown.Item>
                    <Dropdown.Item href="#/action-4">100</Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </Col>

              <Col xs={12} md={6} className="d-flex justify-content-end mb-2">
                <div className="search-container">
                  <Form className="d-flex" onSubmit={handleSubmit}>
                    <Form.Control
                        id="searchQuery"
                        type="text"
                        className="form-control"
                        placeholder="Search Employee"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </Form>
                </div>
              </Col>
            </Row>
          </div>

          <div className="table-responsive">
            {loading ? (
                <p>Loading...</p>
            ) : (
                <table className="table table-striped table-style">
                  <thead className="tablehead">
                  <tr className="table-header-footer">
                    <th className="text-center">SR</th>
                    <th className="text-center">User ID</th>
                    <th className="text-center">Employee Name</th>
                    <th className="text-center">Department</th>
                    <th className="text-center">Job Title</th>
                    <th className="text-center">Email</th>
                    <th className="text-center">Gender</th>
                    <th className="text-center">Status</th>
                    <th className="text-center">Action</th>
                  </tr>
                  </thead>

                  <tbody>
                  {loading ? (
                      <tr>
                        <td colSpan="9">Loading...</td>
                      </tr>
                  ) : searchResults.length > 0 ? (
                      searchResults
                          .slice(
                              (currentPage - 1) * rowsPerPage,
                              currentPage * rowsPerPage
                          )
                          .map((employee, index) => (
                              <tr key={employee._id} style={{ textAlign: "center" }}>
                                <td>{(currentPage - 1) * rowsPerPage + index + 1}</td>
                                <td>{employee.userId}</td>
                                <td>{employee.name}</td>
                                <td>{employee.department?.name || "N/A"}</td>
                                <td>{employee.jobTitle || "N/A"}</td>
                                <td>{employee.email}</td>
                                <td>{employee.gender}</td>
                                <td>
                                  <FcOk />
                                </td>
                                <td>
                                  <div>
                                    <Button
                                        variant="primary"
                                        style={{ marginRight: "5px" }}
                                        onClick={() => handleShow(employee)}
                                        size="sm"
                                    >
                                      <IoIosEye />
                                    </Button>

                                    <Link to={`/edit-emp/${employee.userId}`}>
                                      <Button
                                          style={{ marginRight: "5px" }}
                                          variant="warning"
                                          size="sm"
                                      >
                                        <FaEdit />
                                      </Button>
                                    </Link>
                                  </div>
                                </td>
                              </tr>
                          ))
                  ) : (
                      <tr>
                        <td colSpan="9">No data available</td>
                      </tr>
                  )}
                  </tbody>
                  <tfoot className="table-footer">
                  <tr className="emp-info-table-footer">
                    <td>Total</td>
                    <td>-</td>
                    <td>{searchResults.length}</td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td></td>
                  </tr>
                  </tfoot>
                </table>
            )}
            <Row className="mt-2">
              <Col xs={12} md={6} className="d-flex align-items-center">
                <p className="showing-entries">
                  Showing {(currentPage - 1) * rowsPerPage + 1} to{" "}
                  {Math.min(currentPage * rowsPerPage, searchResults.length)} of{" "}
                  {searchResults.length} entries
                </p>
              </Col>
              <Col xs={12} md={6} className="d-flex justify-content-end">
                <nav>
                  <ul className="pagination">
                    <li
                        className={`page-item ${
                            currentPage === 1 ? "disabled" : ""
                        }`}
                    >
                      <a
                          href="/#"
                          className="page-link"
                          onClick={(e) => {
                            e.preventDefault();
                            handlePageChange(currentPage - 1);
                          }}
                      >
                        Previous
                      </a>
                    </li>
                    {pageRange().map((page) => (
                        <li
                            key={page}
                            className={`page-item ${
                                currentPage === page ? "active" : ""
                            }`}
                        >
                          <a
                              href="/#"
                              className="page-link"
                              onClick={(e) => {
                                e.preventDefault();
                                handlePageChange(page);
                              }}
                          >
                            {page}
                          </a>
                        </li>
                    ))}
                    <li
                        className={`page-item ${
                            currentPage === Math.ceil(searchResults.length / rowsPerPage)
                                ? "disabled"
                                : ""
                        }`}
                    >
                      <a
                          href="/#"
                          className="page-link"
                          onClick={(e) => {
                            e.preventDefault();
                            handlePageChange(currentPage + 1);
                          }}
                      >
                        Next
                      </a>
                    </li>
                  </ul>
                </nav>
              </Col>
            </Row>
          </div>
        </div>

        {/* Modal for displaying employee details */}
        {selectedEmployee && (
          <Modal show={showDialog} onHide={handleClose} className="custom-modal">
            <Modal.Header
                style={{ backgroundColor: "#004aad", color: "#ffffff" }}
            >
              <Modal.Title style={{ color: "#ffffff" }}>
                Employee Details
              </Modal.Title>

              <button
                  onClick={handleClose}
                  style={{
                    color: "white",
                    background: "transparent",
                    border: "none",
                    fontSize: "50px",
                    marginLeft: "50%",
                  }}
              >
                ×
              </button>
            </Modal.Header>

            <Modal.Body>
              {/* Profile Photo Container */}
              <div className="profile-photo-container">
                <h5>Profile Photo</h5>

                {selectedEmployee.profilePhoto ? (
                    <img
                        src={`https://backend.ems.hiradentallab.com/${selectedEmployee.profilePhoto}`}
                        alt="Employee Photo"
                        className="employee-photo rounded-photo"
                    />
                ) : (
                    <p>No profile photo available.</p>
                )}
              </div>

              {/* Employee Info */}
              <div className="employee-info">
                <p>
                  <strong>Name:</strong> {selectedEmployee.name}
                </p>

                <p>
                  <strong>Role:</strong> {selectedEmployee.role}
                </p>

                <p>
                  <strong>User ID:</strong> {selectedEmployee.userId}
                </p>

                <p>
                  <strong>User Name:</strong> {selectedEmployee.username}
                </p>

                <p>
                  <strong>Email:</strong> {selectedEmployee.email}
                </p>

                <p>
                  <strong>Department:</strong> {selectedEmployee.department?.name || "N/A"}
                </p>

                <p>
                  <strong>Job Title:</strong> {selectedEmployee.jobTitle || "N/A"}
                </p>

                <p>
                  <strong>Gender:</strong> {selectedEmployee.gender}
                </p>

                <p>
                  <strong>Address:</strong> {selectedEmployee.address}
                </p>

                <p>
                  <strong>Status:</strong> {selectedEmployee.status}
                </p>

                <p>
                  <strong>Marital Status:</strong> {selectedEmployee.maritalstatus}
                </p>

                <p>
                  <strong>Join Date:</strong>{" "}
                  {new Date(selectedEmployee.joinDate).toLocaleDateString()}
                </p>

                <p>
                  <strong>Birthday According to CNIC:</strong>{" "}
                  {new Date(selectedEmployee.birthday).toLocaleDateString()}
                </p>

                <p>
                  <strong>Work Description:</strong>{" "}
                  {selectedEmployee.workDescription}
                </p>

                <p>
                  <strong>Work Experience:</strong>{" "}
                  {selectedEmployee.workExperience}
                </p>
              </div>

              {/* Documents Section */}
              <div className="documents-section">
                <Row className="justify-content-end">
                  {/* CV Card */}
                  <Col xs={12} md={6} className="text-right">
                    <Card className="custom-card">
                      <Card.Body>
                        <h5>Personal Documents </h5>

                        {selectedEmployee.cv ? (
                            <a
                                href={`https://backend.ems.hiradentallab.com/${selectedEmployee.cv}`}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                              Download Personal Documents
                            </a>
                        ) : (
                            <p>No PD available.</p>
                        )}
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col xs={12} md={6} className="text-right">
                    <Card className="custom-card">
                      <Card.Body>
                        <h5>Company data protection and other Contracts</h5>

                        {selectedEmployee.pastWorkCertificate ? (
                            <a
                                href={`https://backend.ems.hiradentallab.com/${selectedEmployee.pastWorkCertificate}`}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                              View Company Contracts
                            </a>
                        ) : (
                            <p>No Company Contracts available.</p>
                        )}
                      </Card.Body>
                    </Card>
                  </Col>
                  {/* ID Card Front */}
                  <br />
                  <Col xs={12} md={6} className="text-right">
                    <Card className="custom-card">
                      <Card.Body>
                        <h5>HandBook and Employment Contracts</h5>

                        {selectedEmployee.idCardBack ? (
                            <a
                                href={`https://backend.ems.hiradentallab.com/${selectedEmployee.idCardBack}`}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                              View Contracts
                            </a>
                        ) : (
                            <p>No Contracts available.</p>
                        )}
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col xs={12} md={6} className="text-right">
                    <Card className="custom-card">
                      <Card.Body>
                        <h5>Health and Safety Certificates</h5>

                        {selectedEmployee.idCardFront ? (
                            <a
                                href={`https://backend.ems.hiradentallab.com/${selectedEmployee.idCardFront}`}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                              View Certificate
                            </a>
                        ) : (
                            <p>No Certificate available.</p>
                        )}
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              </div>
            </Modal.Body>

            <Modal.Footer>
              <Button variant="secondary" onClick={handleClose}>
                Close
              </Button>
            </Modal.Footer>
          </Modal>
      )}
      </div>
  );
}